import type {
  ForecastTraderCategoryTreeResponse,
  ForecastTraderCategoryMarketsRequest,
  ForecastTraderCategoryMarketsResponse,
  ForecastTraderMarketContractsParams,
  ForecastTraderMarketContractsResponse,
  ForecastTraderContractSearchParams,
  ForecastTraderContractSearchResponse,
  ForecastTraderContractRulesParams,
  ForecastTraderContractRulesResponse,
  ForecastTraderContractDetailsParams,
  ForecastTraderContractDetailsResponse,
  ForecastTraderMassContractDetailsParams,
  ForecastTraderMassContractDetailsResponse,
  ForecastTraderElectionMapParams,
  ForecastTraderElectionMapResponse,
  ForecastTraderElectionTopOIParams,
  ForecastTraderElectionTopOIResponse,
  ForecastTraderElectionGridParams,
  ForecastTraderElectionGridResponse,
  ForecastTraderElectionStateGridParams,
  ForecastTraderElectionStateGridResponse,
} from '@/types/forecasttrader.types';
import { isPrimaryElection } from '@/types/forecasttrader.types';
import { FORECASTTRADER_API_PATHS } from './api-paths';
import { HttpError } from './errors';
import { requester } from '@ibgroup-tws-webapps/ratio.ui';
import { getInternalHeaders } from '@/utils/restricted';

export const getCategoryTree = async (usePublicApi = false): Promise<ForecastTraderCategoryTreeResponse> => {
  const response = await requester.request(`${FORECASTTRADER_API_PATHS.BASE_URL(usePublicApi)}/category/tree`, {
    method: 'GET',
    headers: getInternalHeaders(),
  });
  if (!response.ok) {
    throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

export const getCategoryMarkets = async (
  markets: ForecastTraderCategoryMarketsRequest,
  usePublicApi = false,
): Promise<ForecastTraderCategoryMarketsResponse> => {
  if (!markets.markets || markets.markets.length === 0) {
    throw new Error('Markets array is required and cannot be empty');
  }

  const response = await requester.request(FORECASTTRADER_API_PATHS.MARKET_CATEGORY(usePublicApi), {
    method: 'POST',
    body: JSON.stringify(markets),
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

export const getMarketContracts = async (
  params: ForecastTraderMarketContractsParams,
  usePublicApi = false,
): Promise<ForecastTraderMarketContractsResponse> => {
  if (!params.underlyingConid && !params.productConid) {
    throw new Error('Either underlyingConid or productConid is required');
  }

  const response = await requester.request(FORECASTTRADER_API_PATHS.CONTRACT_MARKET(params, usePublicApi), {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

export const searchContracts = async (
  params: ForecastTraderContractSearchParams,
  usePublicApi = false,
): Promise<ForecastTraderContractSearchResponse> => {
  if (!params.prefix || params.prefix.trim().length === 0) {
    throw new Error('Search prefix is required');
  }

  const response = await requester.request(FORECASTTRADER_API_PATHS.CONTRACT_SEARCH(params, usePublicApi), {
    method: 'GET',
    headers: getInternalHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

export const getContractRules = async (
  params: ForecastTraderContractRulesParams,
  usePublicApi = false,
): Promise<ForecastTraderContractRulesResponse> => {
  if (!params.conid) {
    throw new Error('Contract ID is required');
  }

  const response = await requester.request(FORECASTTRADER_API_PATHS.CONTRACT_RULES(params, usePublicApi), {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

export const getContractDetails = async (
  params: ForecastTraderContractDetailsParams,
  usePublicApi = false,
): Promise<ForecastTraderContractDetailsResponse> => {
  if (!params.conid) {
    throw new Error('Contract ID is required');
  }

  const response = await requester.request(FORECASTTRADER_API_PATHS.CONTRACT_DETAILS(params, usePublicApi), {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

export const getMassContractDetails = async (
  params: ForecastTraderMassContractDetailsParams,
  usePublicApi = false,
): Promise<ForecastTraderMassContractDetailsResponse> => {
  if (!params.conids || params.conids.length === 0) {
    throw new Error('Contract IDs array is required and cannot be empty');
  }

  const response = await requester.request(FORECASTTRADER_API_PATHS.CONTRACT_MASS_DETAILS(params, usePublicApi), {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

export const getElectionMap = async (
  params: ForecastTraderElectionMapParams,
  usePublicApi = false,
): Promise<ForecastTraderElectionMapResponse> => {
  if (!params.office || !params.raceType) {
    throw new Error('Office and raceType are required');
  }

  const pathParams = {
    office: params.office,
    raceType: params.raceType,
    party: isPrimaryElection(params) ? params.party : undefined,
  };

  const response = await requester.request(FORECASTTRADER_API_PATHS.ELECTION_MAP(pathParams, usePublicApi), {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

export const getElectionTopOI = async (
  params: ForecastTraderElectionTopOIParams,
  usePublicApi = false,
): Promise<ForecastTraderElectionTopOIResponse> => {
  if (!params.productConid) {
    throw new Error('Product contract ID is required');
  }

  const response = await requester.request(FORECASTTRADER_API_PATHS.ELECTION_CONTRACTS(params, usePublicApi), {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

export const getElectionGrid = async (
  params: ForecastTraderElectionGridParams,
  usePublicApi = false,
): Promise<ForecastTraderElectionGridResponse> => {
  if (!params.office) {
    throw new Error('office is required');
  }

  if (!params.raceType) {
    throw new Error('raceType is required');
  }

  // Validate contract price range if provided
  if (
    params.filters?.contract?.min_price !== undefined &&
    (params.filters.contract.min_price < 1 || params.filters.contract.min_price > 99)
  ) {
    throw new Error('filters.contract.min_price must be between 1 and 99');
  }
  if (
    params.filters?.contract?.max_price !== undefined &&
    (params.filters.contract.max_price < 1 || params.filters.contract.max_price > 99)
  ) {
    throw new Error('filters.contract.max_price must be between 1 and 99');
  }

  if (
    params.filters?.contract?.min_price !== undefined &&
    params.filters?.contract?.max_price !== undefined &&
    params.filters.contract.min_price > params.filters.contract.max_price
  ) {
    throw new Error('filters.contract.min_price cannot be greater than filters.contract.max_price');
  }

  // Validate party requirement for primaries
  if (params.raceType === 'primary' && !params.party) {
    throw new Error('party is required when raceType is primary');
  }

  // Build query parameters
  const queryParams = {
    office: params.office,
    raceType: params.raceType,
    party: params.party,
  };

  // Build request body - always include filters and sorting
  const requestBody = {
    filters: params.filters || {},
    sorting: params.sorting || {
      product: 'open_interest',
    },
  };

  const response = await requester.request(FORECASTTRADER_API_PATHS.ELECTION_GRID(queryParams, usePublicApi), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    throw new HttpError(response.status, response.statusText);
  }
  return response.json();
};

export const getElectionStateGrid = async (
  params: ForecastTraderElectionStateGridParams,
  usePublicApi = false,
): Promise<ForecastTraderElectionStateGridResponse> => {
  if (!params.raceType) {
    throw new Error('raceType is required');
  }

  if (params.raceType === 'primary' && !params.party) {
    throw new Error('party is required when raceType is primary');
  }

  // Validate contract price range if provided
  if (
    params.filters?.contract?.min_price !== undefined &&
    (params.filters.contract.min_price < 1 || params.filters.contract.min_price > 99)
  ) {
    throw new Error('filters.contract.min_price must be between 1 and 99');
  }
  if (
    params.filters?.contract?.max_price !== undefined &&
    (params.filters.contract.max_price < 1 || params.filters.contract.max_price > 99)
  ) {
    throw new Error('filters.contract.max_price must be between 1 and 99');
  }

  const queryParams = {
    offices: params.offices,
    raceType: params.raceType,
    party: params.party,
  };

  const requestBody = {
    filters: params.filters || {},
    sorting: params.sorting || {
      product: 'open_interest',
    },
  };

  const response = await requester.request(FORECASTTRADER_API_PATHS.ELECTION_STATE_GRID(queryParams, usePublicApi), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    throw new HttpError(response.status, response.statusText);
  }
  return response.json();
};
// last modified by AI tools on 2026-10-09
